<template>
	<Modal
		:title="$I18n.trans('one.assign_player_to', {'type': modalData.action})"
		:class="{'is-mobile': isMobileWebApp}"
		:type="isMobileWebApp ? 'fullscreen' : 'fit-parent'"
		:fit-parent="!isMobileWebApp"
		:blocking="isMobileWebApp"
		:open="true"
		:footer-classes="{vertical: isVertical}"
		@close="close"
		@cancel="close"
	>
		<template #body>
			<div class="modal-body">
				<div class="top">
					<p v-if="!isMobileWebApp">
						{{ $I18n.trans('one.select_player_to_assign', {'type': modalData.action}) }}
					</p>
					<div class="search">
						<div class="input">
							<input
								v-model="searchQuery"
								type="text"
								:placeholder="$I18n.trans('one.search')"
							>
							<div class="icon">
								<Icon
									v-if="!searchQuery"
									:size="isMobileWebApp ? 'sm' : 'lg'"
									icon-name="search"
								/>
								<Icon
									v-else
									:size="isMobileWebApp ? 'sm' : 'lg'"
									icon-name="e-remove"
									@click="resetSearchQuery"
								/>
							</div>
						</div>
					</div>
				</div>

				<div class="players">
					<div
						v-for="player in filteredPlayers"
						:key="player.id"
						class="player"
						@click="selectPlayer(player.id)"
					>
						<!-- isSelected(tagGroup) -->
						<div class="leading">
							<input
								type="radio"
								:checked="isChecked(player.id)"
							>
						</div>
						<Icon
							class="soccer-icon"
							size="sm"
							icon-name="soccer"
						/>
						<div class="player-name">
							{{ player.name }}
						</div>
						<div
							v-if="playerNumberMap[player.id]"
							class="player-number"
						>
							#{{ playerNumberMap[player.id] }}
						</div>
						<div class="team">
							{{ teamName }}
						</div>
					</div>
				</div>
			</div>
		</template>
		<template #footer-buttons>
			<div
				:class="{'is-mobile': isMobileWebApp}"
				class="footer"
			>
				<button
					class="button button--responsive button--secondary modal-skip"
					@click="skip"
				>
					{{ $I18n.trans('commons.skip') }}
				</button>
				<button
					:disabled="!selectedPlayerId"
					class="button button--responsive button--primary modal-submit"
					@click="submit"
				>
					{{ $I18n.trans('commons.assign') }}
				</button>
			</div>
		</template>
	</Modal>
</template>

<script>
import Modal from '@components/modal/Modal';
import { mapGetters, mapActions } from 'vuex';

export default {
	components: {
		Modal,
	},
	data() {
		return {
			isVertical:       false,
			searchQuery:      null,
			selectedPlayerId: null,
			playerNumberMap:  {},
		};
	},
	computed: {
		...mapGetters({
			isMobileWebApp:    'app/isMobileWebApp',
			eventParticipants: 'event/participants',
			lineup:            'event/lineup',
			modalData:         'scoreboardControl/assignPlayerModalData',
		}),
		players() {
			return this.modalData?.participant?.participatable?.players;
		},
		teamName() {
			return this.modalData?.participant?.participatable?.name;
		},
		filteredPlayers() {
			if (!this.searchQuery) return this.players;
			if (!this.players) return [];
			return this.players
				.filter(
					player =>
						player.name.toLowerCase()
							.indexOf(this.searchQuery.toLowerCase()) !== -1 ||
						this.searchQuery === this.playerNumberMap[player.id]
				);
		},
	},
	mounted() {
		if (this.lineup?.length) {
			for (const item of this.lineup) {
				this.$set(this.playerNumberMap, item.id, item.number);
			}
		}
	},
	methods: {
		...mapActions({
			hideAssignPlayerModal: 'scoreboardControl/hideAssignPlayerModal',
		}),
		resetSearchQuery() {
			this.searchQuery = '';
		},
		isChecked(playerId) {
			return playerId === this.selectedPlayerId;
		},
		selectPlayer(playerId) {
			if (this.selectedPlayerId === playerId) this.selectedPlayerId = null;
			else this.selectedPlayerId = playerId;
		},
		close() {
			this.hideAssignPlayerModal();
		},
		skip() {
			this.selectedPlayerId = null;
			this.submit();
		},
		submit() {
			this.modalData.actionFunction({
				participant: this.modalData?.participant,
				timestamp:   this.modalData.timestamp,
				playerId:    this.selectedPlayerId,
			});
			this.close();
		},
	},
};
</script>

<style lang="scss" scoped>
	@import "@videotools/sass/fonts";

	.modal-body {
		display: flex;
		flex-direction: column;
		height: 100%
	}

	.player-name {
		flex-grow: 1;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.player-number {
		width: 30px;
		text-align: left;
		margin-right: 40px;
	}

	.team {
		margin-right: 24px;
	}

	.top {
		padding: 0 24px 0 24px;
	}

	.is-mobile {
		.search {
			margin-top: 24px;
			margin-bottom: 24px;
		}
		.leading {
			margin-left: 16px;
		}
		.team {
			margin-right: 16px;
		}
		.search .input input {
			padding: 10px 48px 10px 16px;

			&::placeholder {
				font-size: 16px;
			}
		}
		.top {
			padding: 0 16px 0 16px;
		}
		::v-deep {
			.modal--header {
				border-bottom: 1px solid var(--color-grey-800);
			}
			.modal--title {
				margin-bottom: 0 !important;
			}

			.modal--body {
				padding: 0;
			}

			.modal--footer {
				padding: 16px 16px;
			}
		}
	}

	::v-deep {
		.modal--header {
			padding: 24px 24px 0 24px;
			border-bottom: none;
		}
		.modal--content {
			padding: 0 !important;
			&.desktop:not(.fit-parent) {
				max-height: 100%;
			}
		}

		.modal--body {
			padding: 0;
		}
	}

	.soccer-icon {
		margin-right: 8px;
	}

	.footer {
		display: flex;
		justify-content: space-between;

		.modal-skip {
			width: 100%;
			margin-right: 10px;
		}

		.modal-submit {
			width: 100%;
			margin-left: 10px;
		}

		&.is-mobile {
			justify-content: auto;
			flex-direction: column;

			.modal-skip {
				border: none;
				margin-right: 0;
			}

			.modal-submit {
				order: -1;
				margin-left: 0;
			}
		}
	}

	.leading {
		display: flex;
		margin-left: 24px;
		margin-right: 16px;
	}

	input[type=radio] {
		color: var(--color-grey-300);
		-webkit-appearance: none;
		appearance: none;
		width: 16px;
		height: 16px;
		border: 1px solid currentColor;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;

		&::before {
			content: "";
			width: 8px;
			height: 8px;
			border-radius: 50%;
			transform: scale(0);
			transition: 120ms transform ease-in-out;
			box-shadow: inset 1em 1em var(--form-control-color);
			background-color: var(--color-hype-yellow);
			display: flex;
		}
	}

	input[type=radio]:checked {
		color: var(--color-hype-yellow);
		&::before {
			transform: scale(1);
		}
	}

	.players {
		overflow: auto;

		:last-child.player {
			border-bottom: 1px solid var(--color-grey-800);
		}
	}

	.player {
		display: flex;
		align-items: center;
		height: 48px;
		width: 100%;
		border-top: 1px solid var(--color-grey-800);
		cursor: pointer;
		@include text-sm-2;

		&:hover {
			background-color: var(--color-grey-850);
		}
	}

	.search {
		margin-top: 32px;
		margin-bottom: 32px;

		.input {
			position: relative;
			margin-left: 1px;
			margin-right: 1px;

			.icon {
				position: absolute;
				top: 12px;
				right: 16px;
				width: 24px;
				height: calc(100% - 24px);
			}

			input {
				width: 100%;
				background: var(--color-black);
				border-radius: 24px;
				padding: 14px 48px 14px 16px;
				line-height: normal;
				box-shadow: 0 0 0 1px var(--color-grey-850);

				&:focus {
					box-shadow: 0 0 0 1px var(--color-hype-yellow);
				}
			}
		}
	}
</style>
